import * as React from "react";
import { useEffect, useState } from "react";
import { vendorApi } from "../../api/api";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Grid,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
  IconButton,
  Popover,
  Button,
  Stack,
  Divider,
  Card,
  CardContent,
  Tooltip,
  Collapse,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import FilterListIcon from '@mui/icons-material/FilterList';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import PersonIcon from '@mui/icons-material/Person';
import LocationCityIcon from '@mui/icons-material/LocationCity';
import MapIcon from '@mui/icons-material/Map';
import NumbersIcon from '@mui/icons-material/Numbers';
import TrackChangesIcon from '@mui/icons-material/TrackChanges';
import VisibilityIcon from '@mui/icons-material/Visibility';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import TuneIcon from '@mui/icons-material/Tune';
import EditIcon from "@mui/icons-material/Edit";
import { Description } from "@mui/icons-material";

const ViewProjectBody = () => {
  const [rows, setRows] = React.useState([]);
  const navigate = useNavigate();
  const [statusFilter, setStatusFilter] = React.useState("All");
  const [searchKeyword, setSearchKeyword] = React.useState("");
  const [pageSize, setPageSize] = useState(25);

  // New filter states
  const [minPlots, setMinPlots] = useState("");
  const [maxPlots, setMaxPlots] = useState("");
  const [minAvailablePlots, setMinAvailablePlots] = useState("");
  const [maxAvailablePlots, setMaxAvailablePlots] = useState("");
  const [cityFilter, setCityFilter] = useState("");
  const [vendorFilter, setVendorFilter] = useState("");

  // Filter popup states
  const [filterAnchorEl, setFilterAnchorEl] = useState(null);
  const [tempSearchKeyword, setTempSearchKeyword] = useState("");
  const [tempStatusFilter, setTempStatusFilter] = useState("All");
  const [tempMinPlots, setTempMinPlots] = useState("");
  const [tempMaxPlots, setTempMaxPlots] = useState("");
  const [tempMinAvailablePlots, setTempMinAvailablePlots] = useState("");
  const [tempMaxAvailablePlots, setTempMaxAvailablePlots] = useState("");
  const [tempCityFilter, setTempCityFilter] = useState("");
  const [tempVendorFilter, setTempVendorFilter] = useState("");

  // Show/hide advanced filters
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);

 const handleAction = (action, row) => {
  if (action === "view") {
    navigate(`/viewProject/viewProject?projectId=${row.id}`, {
      state: {
        ProjectId: row.id,
        ProjectName: row.ProjectName,
        City: row.City,
        Address: row.Address,
        layout_url: row.layout_url,
        NoOfPlots: row.NoOfPlots,
        AvailablePlots: row.AvailablePlots,
        IconUrl: row.IconUrl,
        Description: row.Description,
      },
    });
  }

  if (action === "edit") {
    navigate("/editProject", {
      state: {
        id: row.id,
        ProjectName: row.ProjectName,
        City: row.City,
        Address: row.Address,
        NoOfPlots: row.NoOfPlots,
        Description: row.Description,
        IconUrl: row.IconUrl,
        totalArea:row.totalArea
      },
    });
  }
};

  // Filter popup handlers
  const handleFilterClick = (event) => {
    // Set current filter values to temp values
    setTempSearchKeyword(searchKeyword);
    setTempStatusFilter(statusFilter);
    setTempMinPlots(minPlots);
    setTempMaxPlots(maxPlots);
    setTempMinAvailablePlots(minAvailablePlots);
    setTempMaxAvailablePlots(maxAvailablePlots);
    setTempCityFilter(cityFilter);
    setTempVendorFilter(vendorFilter);
    setFilterAnchorEl(event.currentTarget);
  };

  const handleFilterClose = () => {
    setFilterAnchorEl(null);
  };

  const handleApplyFilters = () => {
    setSearchKeyword(tempSearchKeyword);
    setStatusFilter(tempStatusFilter);
    setMinPlots(tempMinPlots);
    setMaxPlots(tempMaxPlots);
    setMinAvailablePlots(tempMinAvailablePlots);
    setMaxAvailablePlots(tempMaxAvailablePlots);
    setCityFilter(tempCityFilter);
    setVendorFilter(tempVendorFilter);
    handleFilterClose();
  };

  const handleClearFilters = () => {
    setTempSearchKeyword("");
    setTempStatusFilter("All");
    setTempMinPlots("");
    setTempMaxPlots("");
    setTempMinAvailablePlots("");
    setTempMaxAvailablePlots("");
    setTempCityFilter("");
    setTempVendorFilter("");

    // Clear actual filters
    setSearchKeyword("");
    setStatusFilter("All");
    setMinPlots("");
    setMaxPlots("");
    setMinAvailablePlots("");
    setMaxAvailablePlots("");
    setCityFilter("");
    setVendorFilter("");

    handleFilterClose();
  };

  const handleFilterChange = (event) => {
    setStatusFilter(event.target.value);
  };

  useEffect(() => {
    const getProjects = async () => {
      const response = await vendorApi.viewProject();
      const data = response.data;
    
      const formattedData = data.map((item) => ({
        id: item.id,
        ProjectName: item.project_name,
        City: item.city,
        Address: item.address,
        layout_url: item.layout_url,
        NoOfPlots: item.number_of_plots,
        VendorName: item.VendorName || "Admin",
        IconUrl: item.icon_url,
        AvailablePlots: item.available_plots,
        status: item.status,
        Description:item.Description,
        totalArea:item.TotalArea
      }));
      setRows(formattedData);
    };
    getProjects();
  }, []);

  // Get unique cities and vendors for dropdowns
  const uniqueCities = [...new Set(rows.map(row => row.City).filter(Boolean))];
  const uniqueVendors = [...new Set(rows.map(row => row.VendorName).filter(Boolean))];

  const filteredData = rows.filter((row) => {
    const keywordMatch =
      row.ProjectName?.toLowerCase().includes(searchKeyword.toLowerCase()) ||
      row.City?.toLowerCase().includes(searchKeyword.toLowerCase()) ||
      row.VendorName?.toLowerCase().includes(searchKeyword.toLowerCase()) ||
      row.Address?.toLowerCase().includes(searchKeyword.toLowerCase());

    const statusMatch =
      statusFilter === "All" || row.status === statusFilter;

    const minPlotsMatch = minPlots === "" || row.NoOfPlots >= parseInt(minPlots);
    const maxPlotsMatch = maxPlots === "" || row.NoOfPlots <= parseInt(maxPlots);

    const minAvailableMatch = minAvailablePlots === "" || row.AvailablePlots >= parseInt(minAvailablePlots);
    const maxAvailableMatch = maxAvailablePlots === "" || row.AvailablePlots <= parseInt(maxAvailablePlots);

    const cityMatch = cityFilter === "" || row.City === cityFilter;
    const vendorMatch = vendorFilter === "" || row.VendorName === vendorFilter;

    return (
      keywordMatch &&
      statusMatch &&
      minPlotsMatch &&
      maxPlotsMatch &&
      minAvailableMatch &&
      maxAvailableMatch &&
      cityMatch &&
      vendorMatch
    );
  });

  const columns = [
    { field: "id", headerName: "ID", width: 60 },
    { field: "VendorName", headerName: "Vendor Name", flex: 1, minWidth: 150 },
    {
      field: "ProjectName",
      headerName: "Project Name",
      flex: 1,
      minWidth: 160,
      renderCell: (params) => (
        <span
          style={{ color: "blue", cursor: "pointer" }}
          onClick={() => handleAction("view", params.row)}
        >
          {params.value}
        </span>
      ),
    },
    { field: "City", headerName: "City", flex: 1, minWidth: 140 },
    { field: "Address", headerName: "Address", flex: 1, minWidth: 200 },
    { field: "NoOfPlots", headerName: "No Of Plots", width: 120 },
    { field: "AvailablePlots", headerName: "Available Plots", width: 150 },
    {
      field: "status",
      headerName: "Status",
      width: 120,
      renderCell: (params) => {
        const status = params.value;
        let color = "gray";

        switch (status) {
          case "Approved":
            color = "green";
            break;
          case "Sold":
            color = "red";
            break;
          case "Pending":
            color = "#1976d2";
            break;
          default:
            color = "gray";
        }

        return (
          <span
            style={{
              fontWeight: "bold",
              color,
              padding: "4px 8px",
              borderRadius: "8px",
              backgroundColor: `${color}20`,
              display: "inline-block",
              minWidth: "90px",
              textAlign: "center",
            }}
          >
            {status}
          </span>
        );
      },
    },
    {
  field: "actions",
  headerName: "Actions",
  width: 150,
  sortable: false,
  filterable: false,
  disableColumnMenu: true,
  renderCell: (params) => (
    <Stack direction="row" spacing={1}>
      {/* View */}
      <Tooltip title="View Project">
        <IconButton
          size="small"
          onClick={() => handleAction("view", params.row)}
        >
          <VisibilityIcon sx={{ color: "blue" }} />
        </IconButton>
      </Tooltip>

      {/* Edit */}
      <Tooltip title="Edit Project">
        <IconButton
          size="small"
          onClick={() => handleAction("edit", params.row)}
        >
          <EditIcon sx={{ color: "#1976d2" }} />
        </IconButton>
      </Tooltip>
    </Stack>
  ),
},
  ];

  const filterOpen = Boolean(filterAnchorEl);

  return (
    <Box sx={{ width: "100%", mt: 2 }}>
      {/* Single Row Filter Section */}
      <Box>
        <Typography variant="h5" sx={{ fontWeight: "bold", mb: 1, }}>
          Project
        </Typography>

      </Box>
      <Card
        sx={{
          border: "1px solid lightgray",
          borderRadius: "8px",
          mb: 1.5,
          backgroundColor: "#ffffffff",
        }}
      >
        <CardContent sx={{ p: 2 }}>


          {/* Main Filter Row - All in one line */}
          <Grid container spacing={2} alignItems="center">
            {/* Search - 2 columns */}
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                label="Search"
                variant="outlined"
                size="small"
                value={searchKeyword}
                onChange={(e) => setSearchKeyword(e.target.value)}
                InputProps={{
                  startAdornment: <SearchIcon sx={{ mr: 1, color: '#666', fontSize: 20 }} />,
                }}
              />
            </Grid>

            {/* Status - 2 columns */}
            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Status</InputLabel>
                <Select
                  value={statusFilter}
                  onChange={handleFilterChange}
                  label="Status"
                >
                  <MenuItem value="All">All Status</MenuItem>
                  <MenuItem value="Approved">Approved</MenuItem>
                  <MenuItem value="Sold">Sold</MenuItem>
                  <MenuItem value="Pending">Pending</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {/* City - 2 columns */}
            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>City</InputLabel>
                <Select
                  value={cityFilter}
                  onChange={(e) => setCityFilter(e.target.value)}
                  label="City"
                >
                  <MenuItem value="">All Cities</MenuItem>
                  {uniqueCities.map((city) => (
                    <MenuItem key={city} value={city}>{city}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Vendor - 2 columns */}



          </Grid>

          {/* Collapsible Advanced Filters */}

        </CardContent>
      </Card>

      {/* Table Section */}
      <Box
        sx={{
          width: "100%",
          height: { xs: 400, sm: 500, md: 600 },
        }}
      >
        <DataGrid
          rows={filteredData}
          columns={columns}
          pageSize={pageSize}
          pagination
          rowsPerPageOptions={[25, 50, 100]}
          onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
          disableSelectionOnClick
          sx={{
            height: 650,
            border: '1px solid #ccc',
            borderRadius: 2,
            fontSize: '0.9rem',
            backgroundColor: '#ffffff',
            '& .MuiDataGrid-row:hover': {
              backgroundColor: '#f0f0f0ff',
            },
            '& .MuiDataGrid-cell': {
              color: '#333',
              borderBottom: '1px solid #ddd',
            },
            '& .MuiDataGrid-columnHeaders': {
              backgroundColor: '#f0f0f0ff',
              color: 'black',
              fontWeight: 'bold',
            },
            '& .MuiTablePagination-root': {
              fontSize: '0.875rem',
            },
          }}
        />
      </Box>

      {/* Filter Options Popover */}
      <Popover
        open={filterOpen}
        anchorEl={filterAnchorEl}
        onClose={handleFilterClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        PaperProps={{
          sx: {
            width: 400,
            p: 3,
            borderRadius: 2,
            boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
            maxHeight: '80vh',
            overflowY: 'auto',
          }
        }}
      >
        <Box>
          {/* Header */}
          <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
            <Typography variant="h6" sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: 1 }}>
              <FilterListIcon sx={{ color: '#9c27b0' }} />
              Filter Options
            </Typography>
            <IconButton size="small" onClick={handleFilterClose}>
              <CloseIcon />
            </IconButton>
          </Stack>

          <Divider sx={{ mb: 3 }} />

          {/* Search Input */}
          <Box mb={3}>
            <Typography variant="subtitle2" sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
              <SearchIcon fontSize="small" />
              Search Keywords
            </Typography>
            <TextField
              fullWidth
              size="small"
              placeholder="Search projects, cities, vendors..."
              value={tempSearchKeyword}
              onChange={(e) => setTempSearchKeyword(e.target.value)}
            />
          </Box>

          {/* Status Filter */}
          <Box mb={3}>
            <Typography variant="subtitle2" sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
              <TrackChangesIcon fontSize="small" />
              Status
            </Typography>
            <FormControl fullWidth size="small">
              <Select
                value={tempStatusFilter}
                onChange={(e) => setTempStatusFilter(e.target.value)}
              >
                <MenuItem value="All">All Status</MenuItem>
                <MenuItem value="Approved">Approved</MenuItem>
                <MenuItem value="Sold">Sold</MenuItem>
                <MenuItem value="Pending">Pending</MenuItem>
              </Select>
            </FormControl>
          </Box>

          {/* City Filter */}
          <Box mb={3}>
            <Typography variant="subtitle2" sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
              <LocationCityIcon fontSize="small" />
              City
            </Typography>
            <FormControl fullWidth size="small">
              <Select
                value={tempCityFilter}
                onChange={(e) => setTempCityFilter(e.target.value)}
              >
                <MenuItem value="">All Cities</MenuItem>
                {uniqueCities.map((city) => (
                  <MenuItem key={city} value={city}>{city}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          {/* Vendor Filter */}
          <Box mb={3}>
            <Typography variant="subtitle2" sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
              <PersonIcon fontSize="small" />
              Vendor
            </Typography>
            <FormControl fullWidth size="small">
              <Select
                value={tempVendorFilter}
                onChange={(e) => setTempVendorFilter(e.target.value)}
              >
                <MenuItem value="">All Vendors</MenuItem>
                {uniqueVendors.map((vendor) => (
                  <MenuItem key={vendor} value={vendor}>{vendor}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          {/* Number of Plots Range */}
          <Box mb={3}>
            <Typography variant="subtitle2" sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
              <MapIcon fontSize="small" />
              Number of Plots
            </Typography>
            <Stack direction="row" spacing={2}>
              <TextField
                fullWidth
                size="small"
                label="Min Plots"
                type="number"
                value={tempMinPlots}
                onChange={(e) => setTempMinPlots(e.target.value)}
              />
              <TextField
                fullWidth
                size="small"
                label="Max Plots"
                type="number"
                value={tempMaxPlots}
                onChange={(e) => setTempMaxPlots(e.target.value)}
              />
            </Stack>
          </Box>

          {/* Available Plots Range */}
          <Box mb={4}>
            <Typography variant="subtitle2" sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
              <NumbersIcon fontSize="small" />
              Available Plots
            </Typography>
            <Stack direction="row" spacing={2}>
              <TextField
                fullWidth
                size="small"
                label="Min Available"
                type="number"
                value={tempMinAvailablePlots}
                onChange={(e) => setTempMinAvailablePlots(e.target.value)}
              />
              <TextField
                fullWidth
                size="small"
                label="Max Available"
                type="number"
                value={tempMaxAvailablePlots}
                onChange={(e) => setTempMaxAvailablePlots(e.target.value)}
              />
            </Stack>
          </Box>

          {/* Action Buttons */}
          <Stack direction="row" spacing={2} justifyContent="flex-end">
            <Button
              variant="outlined"
              onClick={handleClearFilters}
              sx={{ color: '#f44336', borderColor: '#f44336' }}
            >
              Clear All
            </Button>
            <Button
              variant="contained"
              onClick={handleApplyFilters}
              sx={{ backgroundColor: '#9c27b0', '&:hover': { backgroundColor: '#7b1fa2' } }}
            >
              Apply Filters
            </Button>
          </Stack>
        </Box>
      </Popover>
    </Box>
  );
};

export default ViewProjectBody;